import { Box, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ChartCard from "./ChartCard";
import { COLORS, MOTION_MS, fmtCount, fmtPct, pct, reducedMotion } from "./emsTheme";

const Legend = ({ icon: Icon, color, word, value, share, onClick }) => (
    <Box component={onClick ? "button" : "div"} type={onClick ? "button" : undefined} onClick={onClick}
        sx={{ all: "unset", display: "inline-flex", alignItems: "center", gap: 0.5, cursor: onClick ? "pointer" : "default", borderRadius: "6px", px: 0.5,
            "&:hover": onClick ? { bgcolor: COLORS.ground } : {}, "&:focus-visible": { outline: `2px solid ${COLORS.purple}` } }}>
        <Icon sx={{ fontSize: 16, color }} aria-hidden />
        <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color }}>{word}</Typography>
        <Typography component="span" sx={{ fontSize: 13, color: COLORS.ink, fontVariantNumeric: "tabular-nums" }}>{fmtCount(value)}</Typography>
        <Typography component="span" sx={{ fontSize: 12, color: COLORS.ink3 }}>({share})</Typography>
    </Box>
);

/** One split bar: good part purple, bad part red, both labelled with an icon and a word. */
const Split = ({ label, sub, good, bad, goodWord, badWord, onGood, onBad }) => {
    const total = good + bad;
    const motion = !reducedMotion();
    const g = total ? (good * 100) / total : 0;
    return (
        <Box sx={{ display: "grid", gap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: COLORS.ink }}>{label}</Typography>
                <Typography sx={{ fontSize: 12, color: COLORS.ink3 }}>{sub || `${fmtCount(total)} ${total === 1 ? "ticket" : "tickets"}`}</Typography>
            </Box>
            <Box sx={{ display: "flex", height: 14, borderRadius: "7px", overflow: "hidden", gap: "2px", bgcolor: "#F3F1F7" }} aria-hidden>
                {total > 0 && <Box sx={{ width: `${g}%`, bgcolor: COLORS.purple, transition: motion ? `width ${MOTION_MS}ms` : "none" }} />}
                {total > 0 && <Box sx={{ width: `${100 - g}%`, bgcolor: COLORS.unfavourable, transition: motion ? `width ${MOTION_MS}ms` : "none" }} />}
            </Box>
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                <Legend icon={CheckCircleOutlineIcon} color={COLORS.purple} word={goodWord} value={good} share={fmtPct(pct(good, total), 0)} onClick={onGood} />
                <Legend icon={WarningAmberOutlinedIcon} color={COLORS.unfavourable} word={badWord} value={bad} share={fmtPct(pct(bad, total), 0)} onClick={onBad} />
            </Box>
        </Box>
    );
};

/**
 * In TAT vs Out of TAT (feedback point 12): open tickets on track vs past their resolution TAT,
 * and resolved tickets closed within vs after TAT. Due today is called out because it is the work
 * that turns red next.
 */
const TatHealthCard = ({ summary, cardState, onDrilldown }) => {
    const s = summary || {};
    const open = s.unresolvedCount || 0;
    const past = s.overdueCount || 0;
    const within = s.resolvedWithinTatCount || 0;
    const beyond = s.resolvedBeyondTatCount || 0;
    const due = s.dueTodayCount || 0;
    const title = past ? `${fmtPct(pct(past, open))} of open tickets are past TAT` : open ? "Every open ticket is within TAT" : "No open tickets";
    return (
        <ChartCard {...cardState} eyebrow="In TAT vs out of TAT" title={title} empty={cardState.empty || (!cardState.loading && !cardState.error && !open && !within && !beyond)}
            info="Open: open or pending tickets, split by whether their resolution due time has passed. Resolved: resolved on or before the due time vs after. TAT is set per subcategory (48 hours if none)."
            fileName="ems-in-tat-vs-out-of-tat" height={200} ariaSummary={title}
            table={{ columns: [{ key: "k", label: "" }, { key: "a", label: "In TAT", align: "right" }, { key: "b", label: "Out of TAT", align: "right" }],
                rows: [{ key: "open", k: "Open + pending", a: fmtCount(open - past), b: fmtCount(past) }, { key: "res", k: "Resolved", a: fmtCount(within), b: fmtCount(beyond) }] }}>
            <Box sx={{ display: "grid", gap: 2.5 }}>
                <Split label="Open + pending" good={open - past} bad={past} goodWord="On track" badWord="Past TAT"
                    onBad={past ? () => onDrilldown({ overdueOnly: true }) : undefined} onGood={open - past ? () => onDrilldown({ unresolvedOnly: true }) : undefined} />
                <Split label="Resolved" good={within} bad={beyond} goodWord="Within TAT" badWord="After TAT"
                    onGood={within ? () => onDrilldown({ closedOnly: true }) : undefined} />
                {due > 0 && (
                    <Box component="button" type="button" onClick={() => onDrilldown({ dueTodayOnly: true })}
                        sx={{ all: "unset", display: "inline-flex", alignItems: "center", gap: 0.75, cursor: "pointer", px: 1.25, py: 0.75, borderRadius: "8px", bgcolor: COLORS.orangeSoft, width: "fit-content",
                            "&:focus-visible": { outline: `2px solid ${COLORS.orange}` } }}>
                        <ScheduleIcon sx={{ fontSize: 16, color: COLORS.orangeText }} aria-hidden />
                        <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: COLORS.orangeText }}>Due today: {fmtCount(due)}</Typography>
                        <Typography component="span" sx={{ fontSize: 12, color: COLORS.ink2 }}>turns past TAT if not resolved today</Typography>
                    </Box>
                )}
            </Box>
        </ChartCard>
    );
};

export default TatHealthCard;
